// src/components/user/express/Step2Dropoff.jsx
import React, { useEffect, useRef } from 'react';
import { MapPin, Navigation, User, Phone } from 'lucide-react';

export function Step2Dropoff({
  dropoffAddress, setDropoffAddress,
  setDropoffLat, setDropoffLng,
  recipientName, setRecipientName,
  recipientPhone, setRecipientPhone,
}) {
  const inputRef    = useRef(null);
  const mapRef      = useRef(null);
  const mapInstance = useRef(null);
  const markerRef   = useRef(null);

  useEffect(() => {
    if (!window.google || mapInstance.current) return;
    const map = new window.google.maps.Map(mapRef.current, {
      center: { lat: -12.0464, lng: -77.0428 },
      zoom: 13,
      disableDefaultUI: true,
      zoomControl: true,
      styles: [{ featureType: 'poi', stylers: [{ visibility: 'off' }] }],
    });
    mapInstance.current = map;
    map.addListener('click', (e) => {
      const lat = e.latLng.lat();
      const lng = e.latLng.lng();
      placeMarker(lat, lng);
      reverseGeocode(lat, lng);
    });
  }, []);

  useEffect(() => {
    if (!window.google || !inputRef.current) return;
    const ac = new window.google.maps.places.Autocomplete(inputRef.current, {
      componentRestrictions: { country: 'pe' },
      fields: ['formatted_address', 'geometry'],
    });
    ac.addListener('place_changed', () => {
      const place = ac.getPlace();
      if (!place.geometry) return;
      const lat = place.geometry.location.lat();
      const lng = place.geometry.location.lng();
      setDropoffAddress(place.formatted_address);
      setDropoffLat(lat);
      setDropoffLng(lng);
      placeMarker(lat, lng);
      mapInstance.current?.setCenter({ lat, lng });
      mapInstance.current?.setZoom(16);
    });
  }, []);

  function placeMarker(lat, lng) {
    if (markerRef.current) markerRef.current.setMap(null);
    markerRef.current = new window.google.maps.Marker({
      position: { lat, lng },
      map: mapInstance.current,
      icon: {
        path: window.google.maps.SymbolPath.CIRCLE,
        scale: 10,
        fillColor: '#67A43C',
        fillOpacity: 1,
        strokeColor: '#fff',
        strokeWeight: 2,
      },
    });
    setDropoffLat(lat);
    setDropoffLng(lng);
  }

  function reverseGeocode(lat, lng) {
    const geocoder = new window.google.maps.Geocoder();
    geocoder.geocode({ location: { lat, lng } }, (results, status) => {
      if (status === 'OK' && results[0]) {
        setDropoffAddress(results[0].formatted_address);
        if (inputRef.current) inputRef.current.value = results[0].formatted_address;
      }
    });
  }

  return (
    <div className="space-y-4">
      <div>
        <h2 className="text-lg font-extrabold text-slate-800 font-montserrat mb-1">
          ¿A dónde entregamos?
        </h2>
        <p className="text-sm text-slate-500 font-montserrat">
          Ingresa la dirección de destino y datos del destinatario
        </p>
      </div>

      {/* Autocomplete */}
      <div className="relative">
        <Navigation className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-brand-green" />
        <input
          ref={inputRef}
          type="text"
          placeholder="Buscar dirección de destino…"
          defaultValue={dropoffAddress}
          className="w-full pl-10 pr-4 py-3 rounded-xl border-2 border-slate-200 focus:border-brand-green outline-none text-sm font-montserrat transition-colors"
        />
      </div>

      {/* Map */}
      <div
        ref={mapRef}
        className="w-full h-48 rounded-xl overflow-hidden border-2 border-slate-200"
      />

      {/* Recipient fields */}
      <div className="space-y-3">
        <div className="relative">
          <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <input
            type="text"
            placeholder="Nombre del destinatario *"
            value={recipientName}
            onChange={e => setRecipientName(e.target.value)}
            className="w-full pl-10 pr-4 py-3 rounded-xl border-2 border-slate-200 focus:border-brand-green outline-none text-sm font-montserrat transition-colors"
          />
        </div>
        <div className="relative">
          <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <input
            type="tel"
            placeholder="Teléfono del destinatario *"
            value={recipientPhone}
            onChange={e => setRecipientPhone(e.target.value)}
            className="w-full pl-10 pr-4 py-3 rounded-xl border-2 border-slate-200 focus:border-brand-green outline-none text-sm font-montserrat transition-colors"
          />
        </div>
      </div>
    </div>
  );
}